import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useUIContext from '~/ui/use-ui-context';

import useContextForLocation from './use-context-for-location.js';
import useMainLocationFromContext from './use-main-location-from-context';

/** Hooks providing callbacks for closing a context, current, all or a named
 *
 *
 * Callback takes an options object:
 * replace - if false push will be used. Defaults to false
 * toLocation - if location is to be other than what the main location is.
 */

const useCloseContexts = contextKeys => {
	const { contexts } = useUIContext();
	const location = useLocation();
	const mainLocation = useMainLocationFromContext();
	const navigate = useNavigate();
	const cb = useCallback(
		args => {
			const keys = typeof contextKeys === 'string' ? [contextKeys] : contextKeys;
			const { replace = true, toLocation = undefined } = args || {};
			const defaultLocation = mainLocation ?? location;
			const pathname = !toLocation ? defaultLocation.pathname : toLocation?.pathname ?? toLocation;
			const params = contexts
				.filter(({ key }) => keys.includes(key))
				.reduce((p, { key }) => {
					p.delete(key);
					return p;
				}, new URLSearchParams(window.location.search))
				.toString();
			const url = `${pathname}${params ? `?${params}` : ''}`;
			navigate(url, { ...(replace ? { replace: true } : {}) });
		},
		[navigate, location, contexts, contextKeys, mainLocation]
	);
	return cb;
};

const useCloseAllContexts = () => {
	const { contexts } = useUIContext();
	const keys = contexts.map(({ key }) => key);
	return useCloseContexts(keys);
};

const useCloseCurrentContext = () => {
	const { within } = useUIContext();
	return useCloseContexts([within]);
};

const useCloseContextForLocation = location => {
	const contextKey = useContextForLocation(location)?.key;
	return useCloseContexts(contextKey || []);
};

const CloseContext = () => {
	const cb = useCloseCurrentContext();

	useEffect(() => {
		cb();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

export { useCloseContexts, useCloseAllContexts, useCloseCurrentContext, useCloseContextForLocation, CloseContext };
